import { AgentRunEvent } from '@lucidtech/las-sdk-browser';
import { memo, useMemo } from 'react';

import { ArrowTriangleIcon } from '@/components/icons';
import { useAgentRunTree } from '@/hooks';

import { ExportEventTooltip } from './ExportEventTooltip';
import { PredictionEventTooltip } from './PredictionEventTooltip';
import { ReviewEventTooltip } from './ReviewEventTooltip';
import { TriggerEventTooltip } from './TriggerEventTooltip';

export type AgentRunEventsCellProps = {
  agentId: string;
  runId: string;
};

const IAgentRunEventsCell = ({ agentId, runId }: AgentRunEventsCellProps) => {
  const { triggerEvents, predictionEvents, reviewEvents, exportEvents } = useAgentRunTree({ agentId, runId });

  const arrow = <ArrowTriangleIcon rotation="right" className="size-2 text-gray-300" />;

  const { filteredPredictionEvents, filteredReviewEvents, filteredExportEvents } = useMemo(() => {
    let filteredPredictionEvents = [...predictionEvents];
    let filteredReviewEvents = [...reviewEvents];
    let filteredExportEvents = [...exportEvents];

    if (predictionEvents.length === 0) {
      return { filteredPredictionEvents, filteredReviewEvents, filteredExportEvents };
    }

    const cutoffTimestamp = predictionEvents[predictionEvents.length - 1].timestamp;

    /* Prediction events */
    filteredPredictionEvents = filteredPredictionEvents.filter((e) => e.timestamp >= cutoffTimestamp);

    /* Review events */
    filteredReviewEvents = filteredReviewEvents.filter((e) => e.timestamp >= cutoffTimestamp);

    const uniqueValidationId = (events: AgentRunEvent[], currentEvent: AgentRunEvent) => {
      const exists = !!events.find((e) => e.validationId === currentEvent.validationId);
      if (!exists) events.push(currentEvent);
      return events;
    };

    filteredReviewEvents = filteredReviewEvents.reduceRight(uniqueValidationId, []);
    filteredReviewEvents.reverse();

    /* Export events */
    filteredExportEvents = filteredExportEvents.filter((e) => e.timestamp >= cutoffTimestamp);

    const uniqueActionId = (events: AgentRunEvent[], currentEvent: AgentRunEvent) => {
      const exists = !!events.find((e) => e.actionId === currentEvent.actionId);
      if (!exists) events.push(currentEvent);
      return events;
    };

    filteredExportEvents = filteredExportEvents.reduceRight(uniqueActionId, []);
    filteredExportEvents.reverse();

    return { filteredPredictionEvents, filteredReviewEvents, filteredExportEvents };
  }, [exportEvents, predictionEvents, reviewEvents]);

  return (
    <div className="gap-sm flex items-center">
      {triggerEvents.map((event: AgentRunEvent) => (
        <TriggerEventTooltip event={event} agentId={agentId} runId={runId} key={event.resourceId} />
      ))}
      {filteredPredictionEvents.length > 0 ? arrow : null}
      {filteredPredictionEvents.map((event: AgentRunEvent) => (
        <PredictionEventTooltip event={event} agentId={agentId} runId={runId} key={event.resourceId} />
      ))}
      {filteredReviewEvents.length > 0 ? arrow : null}
      {filteredReviewEvents.map((event: AgentRunEvent) => (
        <ReviewEventTooltip event={event} agentId={agentId} runId={runId} key={event.resourceId} />
      ))}
      {filteredExportEvents.length > 0 ? arrow : null}
      {filteredExportEvents.map((event: AgentRunEvent) => (
        <ExportEventTooltip event={event} agentId={agentId} runId={runId} key={event.resourceId} />
      ))}
    </div>
  );
};

export const AgentRunEventsCell = memo(IAgentRunEventsCell) as typeof IAgentRunEventsCell;
